/**
 * AI Context Builder
 * Assembles live system context from the current page + module state
 * and injects it into every chat request so the AI Manager has full situational awareness.
 *
 * Everything in this payload is read back by the model and repeated to the user
 * as a statement about their own system, so nothing here is hand-typed:
 *
 *  - the HR case snapshot is DERIVED from the cases the HR Case Management store
 *    is holding, counted through the same `computeQueueMetrics` the dashboard and
 *    the queue count through, so the copilot and the screen cannot disagree. It
 *    used to read the `mockCases` array directly and so described sixteen cases
 *    while the queue, which had imported the AI Employee's real runs on top of
 *    them, showed twenty-one;
 *  - the system metrics are FETCHED from the analytics endpoints the app already
 *    calls (`/api/dashboard/pulse`, `/api/agent-hub/data`, `/api/ai/policies`).
 *
 * `buildPageContext` also takes the active SECTION — the sub-view the user is on
 * within the page, published through `useAIPageSection`. Several pages render
 * different screens behind one URL (the Operations Hub tab bar, the Agent Hub
 * tabs, the HR Case Management views), so pathname alone told the copilot almost
 * nothing about what was on the user's screen.
 *
 * `buildPageContext` is synchronous (it is called inline when a message is sent),
 * so the metrics are fetched lazily into a short-lived cache. Until that first
 * fetch resolves the metrics are simply OMITTED from the payload rather than
 * filled with plausible-looking constants — the backend prompt carries the same
 * figures read straight from the database, so an absent key costs nothing while a
 * wrong one costs the demo.
 */

import { apiClient } from '@/lib/api-client'
import {
  mockCases,
  AUTHORED_AVG_RESOLUTION_MINUTES,
} from '@/components/hr-case-management/mocks/cases.mock'
import {
  CASE_TYPE_LABELS,
  computeQueueMetrics,
  isWorkbenchCase,
} from '@/components/hr-case-management/utils/case.utils'
import type { HRCase } from '@/components/hr-case-management/types/case.types'

// ── HR Case Management context ─────────────────────────────────────────────

export interface HRCaseContextSummary {
  totalCases: number
  openCases: number
  inWorkbench: number
  resolvedToday: number
  slaBreached: number
  autonomousResolutionRate: number
  avgResolutionMinutes: number
  workbenchCases: Array<{
    ref: string
    employee: string
    type: string
    slaRemaining: string
  }>
  slaBreachedCases: Array<{
    ref: string
    employee: string
    type: string
  }>
}

// ── System-wide metrics snapshot ──────────────────────────────────────────

export interface SystemMetricsSnapshot {
  headcount: number
  openRequisitions: number
  onboardingActive: number
  atRiskEmployees: number
  policyCompliance: number
  activeOperatorAgents: number
  totalOperatorAgents: number
  activePolicies: number
}

interface PulseKPIResponse {
  total_headcount: number
  open_requisitions: number
  onboarding_active: number
  at_risk_count: number
  compliance_rate: number
}

interface AgentHubResponse {
  summary: {
    total_operators: number
    active_operators: number
  }
}

const METRICS_TTL_MS = 60_000

let metricsCache: SystemMetricsSnapshot | null = null
let metricsFetchedAt = 0
let metricsInFlight: Promise<void> | null = null

async function loadSystemMetrics(): Promise<void> {
  const [pulse, agentHub, policies] = await Promise.all([
    apiClient.get<PulseKPIResponse>('/api/dashboard/pulse'),
    apiClient.get<AgentHubResponse>('/api/agent-hub/data'),
    apiClient.get<unknown[]>('/api/ai/policies?is_active=true'),
  ])

  metricsCache = {
    headcount: pulse.total_headcount,
    openRequisitions: pulse.open_requisitions,
    onboardingActive: pulse.onboarding_active,
    atRiskEmployees: pulse.at_risk_count,
    policyCompliance: pulse.compliance_rate,
    activeOperatorAgents: agentHub.summary.active_operators,
    totalOperatorAgents: agentHub.summary.total_operators,
    activePolicies: Array.isArray(policies) ? policies.length : 0,
  }
  metricsFetchedAt = Date.now()
}

/**
 * Return the cached live metrics, kicking off a refresh when they are missing or
 * stale. Returns null until the first fetch lands — callers must omit the field
 * rather than substitute a default.
 */
export function getSystemMetrics(): SystemMetricsSnapshot | null {
  const stale = Date.now() - metricsFetchedAt > METRICS_TTL_MS
  if ((!metricsCache || stale) && !metricsInFlight && typeof window !== 'undefined') {
    metricsInFlight = loadSystemMetrics()
      .catch(() => {
        // Leave the cache as-is. A failed refresh must never downgrade to
        // invented numbers; the backend prompt still has the live values.
      })
      .finally(() => {
        metricsInFlight = null
      })
  }
  return metricsCache
}

// ── HR Case Management snapshot, derived from the rendered cases ──────────

// Same label the queue renders, so the copilot names a case type exactly as the
// user sees it rather than title-casing the enum.
const caseTypeLabel = (type: keyof typeof CASE_TYPE_LABELS): string => CASE_TYPE_LABELS[type]

function slaRemaining(deadline: string): string {
  const ms = new Date(deadline).getTime() - Date.now()
  if (Number.isNaN(ms)) return 'unknown'
  if (ms <= 0) return 'breached'
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  return `${hours}h ${minutes}m`
}

/**
 * The cases the HR Case Management store is currently holding.
 *
 * PUBLISHED IN, not read out. This module is plain and synchronous; the case
 * store is React context and cannot be reached from a function like this one, so
 * the store pushes its list here whenever the list changes, exactly as the pages
 * push `focusedEntity` below. Null means no store is mounted, and the snapshot
 * then describes the mock array the module ships with, which is what the queue
 * would be rendering in that situation anyway.
 *
 * This is the whole of the fix for the copilot announcing sixteen cases while the
 * screen behind it showed twenty-one: the snapshot was counting the mock array at
 * call time, so every case the store had gained since load, the imported AI
 * Employee runs included, was missing from what the model was told.
 */
let publishedCases: HRCase[] | null = null

export function publishCaseSnapshot(cases: HRCase[] | null): void {
  publishedCases = cases
}

/** "Name (Department)", or just the name when the record carries no department. */
function employeeLabel(c: HRCase): string {
  return c.employee.department ? `${c.employee.name} (${c.employee.department})` : c.employee.name
}

/**
 * Count the live queue, through the one classifier the screen counts through.
 *
 * Every figure below comes from `computeQueueMetrics`, the same function behind
 * the dashboard's KPI band and the Live Queue's chips, so the copilot cannot put
 * a different number on the room's screen than the room is looking at. This file
 * used to hold its own definitions of open and resolved and its own average, and
 * it disagreed with both.
 *
 * THE KEY NAMES ARE A WIRE CONTRACT. `app/services/ai/chat.py` reads
 * `openCases`, `inWorkbench`, `resolvedToday`, `totalCases`, `slaBreached`,
 * `workbenchCases` and the two rates out of this payload by name and renders them
 * into its answers, so they are spelled here exactly as that file expects them.
 * `resolvedToday` is the one that reads wrong: it is the queue's Resolved group,
 * whatever the date, and the backend already labels it "Resolved" when it prints
 * it. Renaming it needs the backend changed in the same commit.
 */
export function buildHRCaseSnapshot(): HRCaseContextSummary {
  const cases = publishedCases ?? mockCases
  const metrics = computeQueueMetrics(cases, {
    avgResolutionMinutes: AUTHORED_AVG_RESOLUTION_MINUTES,
  })
  const workbench = cases.filter(isWorkbenchCase)
  const breached = cases.filter(c => c.slaBreached)

  return {
    totalCases: cases.length,
    openCases: metrics.totalOpen,
    inWorkbench: metrics.inWorkbench,
    resolvedToday: metrics.resolved,
    slaBreached: metrics.slaBreach,
    autonomousResolutionRate: metrics.autonomousResolutionRate,
    avgResolutionMinutes: metrics.avgResolutionMinutes,
    workbenchCases: workbench.map(c => ({
      ref: c.caseRef,
      employee: employeeLabel(c),
      type: caseTypeLabel(c.type),
      slaRemaining: slaRemaining(c.slaDeadline),
    })),
    slaBreachedCases: breached.map(c => ({
      ref: c.caseRef,
      employee: employeeLabel(c),
      type: caseTypeLabel(c.type),
    })),
  }
}

// ── Focused entity ────────────────────────────────────────────────────────

/**
 * The single record the user has open on top of the current view.
 *
 * Published by the page rather than read from a store, because the stores that
 * hold these selections (the employee detail panel, the HR case store) are React
 * context and cannot be read from a plain module function. A page sets this when
 * it opens a detail surface and clears it when that surface closes; anything
 * stale is harmless because the payload also carries the path it was set on.
 */
export interface FocusedEntity {
  type: 'employee' | 'hr_case' | 'requisition' | 'policy'
  id: string
  label?: string
}

let focusedEntity: (FocusedEntity & { path: string }) | null = null

export function setFocusedEntity(path: string, entity: FocusedEntity | null): void {
  focusedEntity = entity ? { ...entity, path } : null
}

function getFocusedEntity(pathname: string): FocusedEntity | null {
  if (!focusedEntity || focusedEntity.path !== pathname) return null
  // Rebuilt field by field rather than spread-minus-path, so the payload can
  // never carry the internal path key out to the model.
  const { type, id, label } = focusedEntity
  return label ? { type, id, label } : { type, id }
}

// ── What is actually rendered on each view ────────────────────────────────

/**
 * Widget names per `route::section`, so the copilot can describe the screen the
 * user is looking at rather than the module it belongs to.
 *
 * Deliberately short. The authoritative, fully described list lives in
 * `app/services/ai/page_context.py`, which the backend injects for the current
 * view; this exists so the payload is self-describing even when the two are read
 * apart. Keep the section keys identical to the ones in that registry.
 */
const VISIBLE_WIDGETS: Record<string, string[]> = {
  '/::': ['AIStatusBar', 'CommandMetrics', 'DailyBriefing', 'DecisionQueue', 'ActivityStream', 'WorkforcePulse'],

  '/workbench::requisitions': ['AIContextBar', 'KpiBand', 'RequisitionInsights', 'Requisition table'],
  '/workbench::onboarding': ['AIContextBar', 'KpiBand', 'OnboardingInsights', 'New hire cards with task breakdown'],
  '/workbench::my-team': ['AIContextBar', 'KpiBand', 'Filter pills', 'Employee card grid', 'EmployeeDetailPanel'],
  '/workbench::retention-radar': [
    'AIContextBar',
    'RetentionKpiBand',
    'DepartmentRiskHeatmap',
    'RiskDriverBreakdown',
    'InterventionTracker',
    'FlightRiskQueue',
    'Employee360View',
  ],
  '/workbench::separations': ['AIContextBar', 'KpiBand', 'SeparationInsights', 'Separation table with exit interview rows'],

  '/hr-case-management::dashboard': ['CaseDashboard'],
  '/hr-case-management::queue': ['CaseQueue'],
  '/hr-case-management::detail': ['CaseDetail'],
  '/hr-case-management::chat': ['UserChatView'],

  '/agent-hub::map': ['OrchestrationMap'],
  '/agent-hub::grid': ['AgentGrid', 'OperatorDetailModal'],
  '/agent-hub::performance': ['PerformanceTab'],

  '/ai/policies::policies': ['PolicyCard grid', 'PolicyDetailModal'],
  '/ai/policies::create-ai': ['CreateWithAI', 'UploadPolicyDoc'],
  '/ai/policies::structured': ['StructuredBuilder'],
  '/ai/policies::matrix': ['PermissionMatrixTab'],
  '/ai/policies::case-mgmt': ['CaseManagementPoliciesTab'],

  '/ai/insights::summary': ['InsightCard list'],
  '/ai/insights::patterns': ['PatternCluster'],
  '/ai/insights::actions': ['ActionCard list'],
  '/ai/insights::case-mgmt': ['CaseManagementPoliciesTab'],
}

function visibleWidgets(pathname: string, section: string | null): string[] | undefined {
  const exact = VISIBLE_WIDGETS[`${pathname}::${section ?? ''}`]
  if (exact) return exact
  // A page with no sections registers under an empty section key.
  return VISIBLE_WIDGETS[`${pathname}::`]
}

// ── Build context payload per page ────────────────────────────────────────

export function buildPageContext(
  pathname: string,
  section: string | null = null,
): Record<string, unknown> {
  const metrics = getSystemMetrics()
  const widgets = visibleWidgets(pathname, section)
  const focused = getFocusedEntity(pathname)

  const base: Record<string, unknown> = {
    timestamp: new Date().toISOString(),
    // Null rather than omitted: "no sub-view" is a fact about the screen, and
    // the backend registry treats a missing section and an absent one the same.
    active_section: section,
    ...(widgets ? { visible_widgets: widgets } : {}),
    ...(focused ? { focused_entity: focused } : {}),
  }
  if (metrics) {
    base.system_metrics = metrics
  } else {
    base.system_metrics_note =
      'Live metrics not yet loaded in the browser. Use the figures in the LIVE SYSTEM STATE block; state no metric from memory.'
  }

  if (pathname === '/' || pathname === '') {
    return {
      ...base,
      module: 'command_briefing',
      focus: 'executive_dashboard',
    }
  }

  if (pathname.startsWith('/hr-case-management')) {
    return {
      ...base,
      module: 'hr_case_management',
      focus: section ?? 'l1_l2_case_operations',
      case_queue: buildHRCaseSnapshot(),
      case_queue_source: 'Derived from the case records this module is rendering. Authoritative for every case ref, owner, count and SLA state.',
      routing_note: 'User is actively managing HR cases. Prioritize L1/L2 classification, SLA status, and workbench items.',
    }
  }

  if (pathname.startsWith('/workbench')) {
    return {
      ...base,
      module: 'operations_hub',
      // The tab the user is on, not the hub in general. Answering "what is this
      // page about?" with a five-tab blurb was the defect this replaces.
      focus: section ?? 'workforce_operations',
      ...(metrics
        ? {
            quick_stats: {
              open_requisitions: metrics.openRequisitions,
              onboarding_active: metrics.onboardingActive,
              at_risk_employees: metrics.atRiskEmployees,
            },
          }
        : {}),
    }
  }

  if (pathname.startsWith('/ai/policies')) {
    return {
      ...base,
      module: 'ai_policies',
      focus: section ?? 'governance_rules',
      ...(metrics ? { active_policies: metrics.activePolicies } : {}),
    }
  }

  if (pathname.startsWith('/agent-hub')) {
    return {
      ...base,
      module: 'agent_hub',
      focus: section ?? 'operator_agents',
      ...(metrics
        ? {
            agents_online: metrics.activeOperatorAgents,
            agents_total: metrics.totalOperatorAgents,
          }
        : {}),
    }
  }

  if (pathname.startsWith('/ai/insights')) {
    return {
      ...base,
      module: 'ai_insights',
      focus: section ?? 'pattern_analysis',
    }
  }

  return base
}
